import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,  
    CircularProgress, 
    Tooltip, 
    IconButton,
} from '@mui/material';
import { 
    Download, 
    Visibility, 
} from '@mui/icons-material';

import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchGroupedRosterDetailsByCrewCode } from 'state/api';
import { filterCrewMember, formatNumber } from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import { buildPermissionMap, hasRight } from 'state/checkPermission';

import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import CrewFilterDialog from 'components/CrewFilterDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import CardList from 'components/CardList';


const CrewMemberSales = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);  
    const crewRanksData = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const { startDate, endDate } = useDateRangeContext();

    const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    const canView = hasRight(permissionMap, "CrewMemberSales", "view", false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
        isActive: 'ALL',
    };
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);

    const [selectedCrewCode, setSelectedCrewCode] = useState(null);
    const [selectedCrewName, setSelectedCrewName] = useState(null);
    const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
    const [crewMemberSales, setCrewMemberSales] = useState([]);

    
    useEffect(() => {        
        const handleCrewMemberSales = async () => {
            if (!startDate || !endDate) return [];
            
            try {           
                setError(null); 
                setLoading(true);
                const url = '/inflightsales/crewmembersales';
                const payload = { startDate, endDate, timeMode };               
                const res = await api.post(url, payload);
                setCrewMemberSales(res.data);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }; 
        
        handleCrewMemberSales();      
    }, [timeMode, startDate, endDate]); 

    const filteredCrewMembers = useMemo(() => {
        if (!Array.isArray(crewMemberSales)) {
            return []; 
        }
        
        if (!filters) return crewMemberSales; 

        return crewMemberSales.filter(member => filterCrewMember(member, filters));
    }, [crewMemberSales, filters]);  
       
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };    
         
    const {
        baseOptions,
        postOptions,
        trainingQualOptions,
    } = useMemo(() => {
        if (!crewMemberSales?.length) {
            return {
                baseOptions: [],
                postOptions: [],
                trainingQualOptions: [],
            };
        }
    
        const bases = new Set();
        const posts = new Set();
        const trainingQuals = new Set();
    
        for (const m of crewMemberSales) {
            if (m?.base) bases.add(m.base);
            if (m?.post) posts.add(m.post);        
            m?.trainingQuals?.forEach(q => q && trainingQuals.add(q));
        }
    
        const rankMap = new Map(
            crewRanksData.map(r => [r.rankCode, r])
        );
    
        const sortedPosts = [...posts]
            .map(r => rankMap.get(r))
            .filter(Boolean)
            .sort((a, b) => a.rankPriority - b.rankPriority)
            .map(r => r.rankCode);
    
        return {
            baseOptions: [...bases].sort(),
            postOptions: sortedPosts,
            trainingQualOptions: [...trainingQuals].sort(),
        };
    }, [crewMemberSales, crewRanksData]);

    const handleExcelJSDownload = () => {
        if (!crewMemberSales || crewMemberSales.length === 0) {
            toast.error("No data available to export.");
            return;
        }
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14); // YYYYMMDDHHMMSS
        const fileName = `CrewSales_${timestamp}.xlsx`;

        exportToExcel(crewMemberSales, fileName);
    };
    
    const handleViewClick = useCallback(async (crewCode) => {
        try {
            const result = await fetchGroupedRosterDetailsByCrewCode(
                crewCode, 
                startDate, 
                endDate
            );
            
            setGroupedCrewRosters(result);
            setOpenRosterDetailsDialog(true);
        } catch (error) {
            setOpenRosterDetailsDialog(false);
        } 
    }, [startDate, endDate]); 
    
    const handleCloseRosterDetailsDialog = () => {
        setOpenRosterDetailsDialog(false);
        setGroupedCrewRosters([]);
    };

    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            headerClassName: 'theme-column--header',
            filterable: false,      
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },
        {
            field: "crewCode",
            headerName: "Code",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },
        {
            field: "crewName",
            headerName: "CrewName",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 300 } : { flex: 4 };
            })(),
        },        
        {
            field: "post",
            headerName: "Post",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        {
            field: "base",
            headerName: "Base",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1.2 };
            })(),
        },
        // {
        //     field: "trainingQuals",
        //     headerName: "Trainer",
        //     ...(() => {
        //         return isMobile ? { width: 80 } : { flex: 1.2 };
        //     })(),
        // },
        // {
        //     field: "quals",
        //     headerName: "Quals",
        //     ...(() => {
        //         return isMobile ? { width: 150 } : { flex: 2.5 };
        //     })(),
        // },
        {
            field: "preorderAmount",
            headerName: "PreorderAmount",
            headerClassName: 'theme-column--header',
            align: 'right',            
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },
        {
            field: "directAmount",
            headerName: "DirectAmount",
            headerClassName: 'theme-column--header',
            align: 'right',
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },
        {
            field: "totalAmount",
            headerName: "TotalAmount",
            headerClassName: 'theme-column--header',
            align: 'right',
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },
        {
            field: "preorderCommission",
            headerName: "PreorderCommission",
            headerClassName: 'theme-column--header',
            align: 'right',            
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },
        {
            field: "directCommission",
            headerName: "DirectCommission",
            headerClassName: 'theme-column--header',
            align: 'right',
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },
        {
            field: "totalCommission",
            headerName: "TotalCommission",
            headerClassName: 'theme-column--header',
            align: 'right',
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },        
    ], [isMobile]);
    

    const actionColumn = useMemo(() => {
        const activeCount = [canView].filter(Boolean).length;
        if (activeCount === 0) return [];
        const baseWidth = 60 + activeCount * 25;

        return [{
            field: "action",
            headerName: "Action",
            width: baseWidth,
            filterable: false,      
            sortable: false,
            renderCell: (params) => (
                <FlexCenter
                    gap={1}
                    sx={{ width: '100%', height: '100%' }}
                >         
                    {canView && (
                        <Tooltip title="View">
                            <IconButton
                                aria-label="View"
                                sx={{ color: "primary.dark" }}
                                onClick={() => {
                                    setSelectedCrewCode(params.row.crewCode);
                                    setSelectedCrewName(params.row.crewName);
                                    handleViewClick(params.row.crewCode);
                                }}
                            >
                                <Visibility />
                            </IconButton>
                        </Tooltip>
                    )}   
                </FlexCenter>
            ),
        }]
    }, [canView, handleViewClick]); 

    const columnsToShow = [...columns, ...actionColumn];

    
    return (
        <Box className="main-container-box">            
            <FlexBetween>
                <Header 
                    title="Crew Sales" 
                    subtitle={`${loading ? 'Loading...' : 'List of Crew Sales'}`}
                /> 
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton 
                                    onClick={() => {
                                    handleExcelJSDownload();
                                    logClientAction("EXPORT_REPORT", { fileName: "CrewSales", format: "Excel", startDate, endDate });
                                }}
                                >
                                    <Download />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        
                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>              
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12" }}
                    >
                        {isMobile ? (
                            <CardList
                                rows={filteredCrewMembers}
                                primaryField="crewName"
                                secondaryField="crewCode"
                                fields={[
                                    { label: "Post",               key: "post" },
                                    { label: "Base",               key: "base" },
                                    { label: "Preorder Amount",    key: "preorderAmount",     render: (r) => formatNumber(r.preorderAmount) },
                                    { label: "Direct Amount",      key: "directAmount",       render: (r) => formatNumber(r.directAmount) },
                                    { label: "Total Amount",       key: "totalAmount",        render: (r) => formatNumber(r.totalAmount) },
                                    { label: "Preorder Commission",key: "preorderCommission", render: (r) => formatNumber(r.preorderCommission) },
                                    { label: "Direct Commission",  key: "directCommission",   render: (r) => formatNumber(r.directCommission) },
                                    { label: "Total Commission",   key: "totalCommission",    render: (r) => formatNumber(r.totalCommission) },
                                ]}
                                isActiveField={null}
                                canView={canView}
                                canEdit={false}
                                canDelete={false}
                                onView={(id) => {
                                    const m = filteredCrewMembers.find(x => x.crewCode === id || x._id === id);
                                    if (m) {
                                        setSelectedCrewCode(m.crewCode);
                                        setSelectedCrewName(m.crewName);
                                        handleViewClick(m.crewCode);
                                    }
                                }}
                                minWidth={130}
                            />
                        ) : (
                            <DataGridComponent
                                rows={filteredCrewMembers}
                                columns={columnsToShow}
                                loading={loading}
                                props={{
                                    getRowId: (row) => row.crewCode,
                                    initialState: {
                                        pagination: { paginationModel: { pageSize: 100 } },
                                    },
                                }}
                            />
                        )}
                    </Box>              
                </Box>
            )}

            {isFilterDialogOpen &&
                <CrewFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    postOptions={postOptions}
                    baseOptions={baseOptions}
                    trainingQualOptions={trainingQualOptions}
                    currentFilters={filters}
                />
            }

            {openRosterDetailsDialog &&
                <RosterDetailsDialog 
                    open={openRosterDetailsDialog}
                    onClose={handleCloseRosterDetailsDialog}
                    crewCode={selectedCrewCode}
                    crewName={selectedCrewName}
                    crewRosters={groupedCrewRosters}
                />
            }
        </Box>
    );
};

export default CrewMemberSales;
